import React, { FC, useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { OrganizationFetcher, OrganizationProductsFetcher } from '../Customer/CustomerFetchers';
import { createFetcher, useAutoFetch } from '../../contexts/Fetcher';
import { DateFormat } from '../../helpers/dateFormat';
import Checkbox from '@mui/material/Checkbox';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { PageHeader } from '../PageHeader/PageHeader';
import { CustomerProductRenew } from './CustomerProductRenew';
import { CustomerProductEdit } from './CustomerProductEdit';
import { CustomerProductSuspend } from './CustomerProductSuspend';
import { ConfirmationDialog } from '../ConfirmationDialog/ConfirmationDialog';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { useTranslation } from 'react-i18next';
import { useIntParams } from '../../hooks/useIntParams';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import dayjs, { Dayjs } from 'dayjs';
import { apiOrganizationProduct } from '../../resources/api/apiOrganizationProduct';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';
import { EOrganizationSelectionStatus, OrganizationContext } from '../../contexts/OrganizationContext';
import { LicensesTable } from '../LicensesTable/LicensesTable';
import Button from '@mui/material/Button';
import { Link as RouterLink } from 'react-router-dom';
import { useDialog } from '../../hooks/useDialog';
import { DangerousButton } from '../CustomButtons/DangerousButton';
import { useIsGuardedRouteAllowed } from '../../resources/GuardedRouteLink';
import { CustomerProductLicensesFetcher } from '../../fetchers/CustomerProductLicensesFetcher';
import { Date } from '../Date/Date';
import Alert from '@mui/material/Alert';
import Box from '@mui/system/Box';
import { ResourceDenied } from '../../resources/resource';
import { LicensesContextFromCustomerProductLicenses } from '../../contexts/LicensesContextProviders';
import { OrganizationContextProviderFromSelf } from '../../contexts/OrganizationContextProvider';

interface IUrlParams {
  customer_product_id: string;
}

const {
  useGetOrganizationProduct,
  useReinstateOrganizationProduct,
  useRevokeOrganizationProduct,
  useRenewOrganizationProduct,
  useSuspendOrganizationProduct,
  useUpdateOrganizationProduct,
} = apiOrganizationProduct;

export const CustomerProductFetcher = createFetcher(() => {
  const { customer_product_id } = useIntParams<IUrlParams>();
  const { organization } = useContext(OrganizationContext);

  const { execute } = useGetOrganizationProduct(organization ?? void 0, customer_product_id);

  return execute;
});

const useStyles = makeStyles((theme) =>
  createStyles({
    buttonsContainer: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    checkboxesContainer: {
      display: 'flex',
      gap: theme.spacing(1),
      marginTop: theme.spacing(2),
      justifyContent: 'flex-start',
      width: '100%',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
    },
  })
);

const CustomerProductRenewalDetails: FC<{ currentExpiry: Dayjs | null }> = ({ currentExpiry }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { data: productData } = useContext(CustomerProductFetcher.Context);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div className={styles.checkboxesContainer}>
          <Grid container direction="column" spacing={1}>
            <Grid item>
              {t('common.tableHeader.expires')}:{' '}
              {currentExpiry ? <Date date={currentExpiry} format={DateFormat.DateFullWithTimeAndZone} /> : '-'}
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={!!productData?.properties.notification_expiring_30_days} disabled />}
                label={t('customerProduct.notificationEditDialog.notification_expiring_30_days')}
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={!!productData?.properties.notification_reneval_30_days} disabled />}
                label={t('customerProduct.notificationEditDialog.notification_reneval_30_days')}
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={!!productData?.properties.notification_reneval_60_days} disabled />}
                label={t('customerProduct.notificationEditDialog.notification_reneval_60_days')}
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={!!productData?.properties.notification_reneval_90_days} disabled />}
                label={t('customerProduct.notificationEditDialog.notification_reneval_90_days')}
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={!!productData?.properties.notification_reneval_180_days} disabled />}
                label={t('customerProduct.notificationEditDialog.notification_reneval_180_days')}
              />
            </Grid>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={!!productData?.properties.notification_reneval_270_days} disabled />}
                label={t('customerProduct.notificationEditDialog.notification_reneval_270_days')}
              />
            </Grid>
          </Grid>
        </div>
      </Grid>
    </Grid>
  );
};

const CustomerProductContent: FC = () => {
  const { organization, selectionStatus: organizationSelectionType } = useContext(OrganizationContext);
  const { data: licensesData, onRefresh: onRefreshLicenses } = useContext(CustomerProductLicensesFetcher.Context);

  const { customer_product_id } = useIntParams<IUrlParams>();
  const { data: productsData, onRefresh } = useContext(OrganizationProductsFetcher.Context);

  const product = useMemo(() => {
    return productsData?.customerProducts.find(({ id }) => id === customer_product_id);
  }, [productsData, customer_product_id]);

  const { Guard: GuardProductDetails } = useGetOrganizationProduct(organization ?? void 0, product?.id);

  const history = useHistory();
  const styles = useStyles();

  const { execute: executeReinstate, Guard: GuardReinstate } = useReinstateOrganizationProduct(
    organization ?? void 0,
    product?.id
  );
  const { execute: executeRevoke, Guard: GuardRevoke } = useRevokeOrganizationProduct(
    organization ?? void 0,
    product?.id
  );
  const { Guard: GuardRenew } = useRenewOrganizationProduct(organization ?? void 0, product?.id);
  const { Guard: GuardSuspend } = useSuspendOrganizationProduct(organization ?? void 0, product?.id);
  const { Guard: GuardEdit } = useUpdateOrganizationProduct(organization ?? void 0, product?.id);

  const handleError = useApiErrorHandler();
  const { t } = useTranslation();
  useAutoFetch(OrganizationFetcher);

  const reinstate = useCallback(async () => {
    if (!organization || !product) return;
    try {
      await executeReinstate();
      await Promise.all([onRefresh(), onRefreshLicenses()]);
      abortReinstate();
    } catch (e) {
      handleError(e);
    }
  }, [executeReinstate, organization, product]);

  const revoke = useCallback(async () => {
    if (!organization || !product) return;
    try {
      await executeRevoke();
      history.push(AuthenticatedRoutes.OrganizationDetails(organization.name));
    } catch (e) {
      handleError(e);
    }
  }, [executeRevoke, organization, product]);

  const currentExpiry = product?.expiresAt ? dayjs(product.expiresAt * 1000) : null;

  const { isOpen: isRenewOpen, openUrl: renewUrl, close: abortRenew } = useDialog('renew');
  const { isOpen: isReinstateOpen, openUrl: reinstateUrl, close: abortReinstate } = useDialog('reinstate');
  const { isOpen: isSuspendOpen, openUrl: suspendUrl, close: abortSuspend } = useDialog('suspend');
  const { isOpen: isRevokeOpen, openUrl: revokeUrl, close: abortRevoke } = useDialog('revoke');
  const { isOpen: isEditOpen, openUrl: editUrl, close: abortEdit } = useDialog('edit');

  const isOrganizationsAllowed = useIsGuardedRouteAllowed('Organizations', []);
  const isOrganizationProductsAllowed = useIsGuardedRouteAllowed('OrganizationDetailsProducts', []);

  if (!organization || !licensesData) return null;

  return (
    <div>
      <PageHeader
        title={product?.planName ?? ''}
        crumbs={
          organizationSelectionType === EOrganizationSelectionStatus.Injected
            ? [
                isOrganizationsAllowed
                  ? {
                      url: AuthenticatedRoutes.Organizations(),
                      title: t('layout.navigation.organizations'),
                    }
                  : null,
                isOrganizationProductsAllowed
                  ? {
                      url: AuthenticatedRoutes.OrganizationDetailsProducts(organization?.name),
                      title: organization.name,
                    }
                  : null,
              ]
            : [
                {
                  url: AuthenticatedRoutes.CurrentOrganizationProducts(),
                  title: t('layout.navigation.products'),
                },
              ]
        }
      >
        <div className={styles.buttonsContainer}>
          <GuardRenew>
            <Button
              component={RouterLink}
              to={renewUrl}
              color="primary"
              variant="contained"
              disabled={product?.corrupted}
            >
              {t('customerProduct.renewButton')}
            </Button>

            {product ? (
              <CustomerProductRenew open={isRenewOpen} closeDialog={abortRenew} customerProduct={product} />
            ) : null}
          </GuardRenew>

          <GuardEdit>
            <Button
              component={RouterLink}
              to={editUrl}
              color="primary"
              variant="contained"
              disabled={product?.corrupted}
            >
              {t('customerProduct.editButton')}
            </Button>
          </GuardEdit>

          {product?.suspended ? (
            <GuardReinstate>
              <Button
                component={RouterLink}
                to={reinstateUrl}
                variant="outlined"
                color="primary"
                disabled={product?.corrupted}
                replace={true}
              >
                {t('customerProduct.reinstateButton')}
              </Button>

              <ConfirmationDialog
                open={isReinstateOpen}
                title={t('customerProduct.reinstateDialog.title')}
                message={t('customerProduct.reinstateDialog.message')}
                confirm={reinstate}
                abort={abortReinstate}
              />
            </GuardReinstate>
          ) : (
            <GuardSuspend>
              <Button component={RouterLink} to={suspendUrl} variant="outlined" disabled={product?.corrupted}>
                {t('customerProduct.suspendButton')}
              </Button>

              {product ? (
                <CustomerProductSuspend open={isSuspendOpen} closeDialog={abortSuspend} customerProduct={product} />
              ) : null}
            </GuardSuspend>
          )}
          <GuardRevoke>
            <DangerousButton
              component={RouterLink}
              to={revokeUrl}
              disabled={licensesData.some((license) => !!license.userUuid)}
              replace={true}
            >
              {t('customerProduct.revokeButton')}
            </DangerousButton>

            <ConfirmationDialog
              open={isRevokeOpen}
              title={t('customerProduct.revokeDialog.title')}
              message={t('customerProduct.revokeDialog.message')}
              confirm={revoke}
              abort={abortRevoke}
              dangerous
            />
          </GuardRevoke>
        </div>
      </PageHeader>

      <GuardProductDetails>
        <CustomerProductFetcher.WAF>
          <CustomerProductRenewalDetails currentExpiry={currentExpiry} />
          <CustomerProductEdit open={isEditOpen} closeDialog={abortEdit} />
        </CustomerProductFetcher.WAF>
      </GuardProductDetails>

      {product?.corrupted ? (
        <Box mt={1} mb={1}>
          <Alert severity={'error'}>
            <GuardRevoke>
              {t('customerProduct.corrupted.messageAdmin')}
              <ResourceDenied>{t('customerProduct.corrupted.messageOwner')}</ResourceDenied>
            </GuardRevoke>
          </Alert>
        </Box>
      ) : null}

      <LicensesTable />
    </div>
  );
};

export const OrganizationProduct: FC = () => {
  return (
    <OrganizationProductsFetcher.WAF>
      <CustomerProductLicensesFetcher.WAF>
        <LicensesContextFromCustomerProductLicenses>
          <OrganizationContextProviderFromSelf>
            <CustomerProductContent />
          </OrganizationContextProviderFromSelf>
        </LicensesContextFromCustomerProductLicenses>
      </CustomerProductLicensesFetcher.WAF>
    </OrganizationProductsFetcher.WAF>
  );
};
